import {
  Breadcrumbs,
  Footer,
  Header,
  useChat,
  useMapi,
} from '@leshen/gatsby-theme-leshen'
import { Main, SEO } from '@leshen/ui'
import React, { useEffect } from 'react'
import Helmet from 'react-helmet'

import { Link } from 'gatsby'

import { mapRichTextHero, mapSections } from '../../utils/determiners'
import {
  footerMutations,
  headerMutations,
} from '../../utils/contentfulMutations'

import buildSeoSchema from '../../utils/buildSeoSchema'
import getCanonical from '../../utils/getCanonical'

const DefaultTemplate = ({
  data,
  titleTemplate,
  defaultRobots,
  main,
  disableBreadcrumbs,
  type,
}) => {
  const {
    seo,
    header,
    hero,
    footer,
    path,
    promoCode,
    ringPool,
    spanish,
    hideBreadcrumbs,
    customBreadCrumbs,
    sections,
    shortName,
  } = data?.contentfulPage || {}

  const packages = data.allContentfulPackage

  const {
    siteURL,
    siteName,
    alternateName,
    removeTrailingSlashes,
    vwoProjectID,
  } = data?.site.siteMetadata || {}

  const newHeaderData = headerMutations(header)
  const newFooterData = footerMutations(footer)

  const isClient = typeof window !== 'undefined'

  const breadcrumbProps = !disableBreadcrumbs && {
    path,
    crumbs: customBreadCrumbs
      ? // if using custom breadcrumbs, then last breadcrumb is the current page and its short name. unless no short name is given then use the url
        [
          ...customBreadCrumbs,
          {
            path,
            shortName: shortName || path.split('/').pop().replaceAll('-', ' '),
          },
        ]
      : null,
    siteURL,
    removeTrailingSlashes,
    CustomLink: Link,
  }

  const heroOptions = {
    mapper: mapRichTextHero,
    isImageCritical: true,
    spanish,
  }

  const sectionOptions = {
    spanish,
  }

  const { passPromoCode, passRingPool, brandToken } = useMapi()

  useChat()

  // Calling this before passPromoCode so that ringPoolTokens are set before
  useEffect(() => {
    if (ringPool && isClient) {
      passRingPool(ringPool)
    }
  }, [ringPool, brandToken, passRingPool, isClient])

  // Setup Mapi bootstrap with page promo code or default from site config
  // Calling setPromoCode this way avoids multiple calls to Mapi
  useEffect(() => {
    if (isClient) {
      passPromoCode(promoCode)
    }
  }, [promoCode, passPromoCode, isClient])

  return (
    <>
      <Helmet>
        {/* doing this conditional twice because Helmet wasn't playing nice with a fragment */}
        {vwoProjectID && (
          <link
            rel="preconnect"
            href="https://dev.visualwebsiteoptimizer.com"
          />
        )}
        {vwoProjectID && (
          <script type="text/javascript" id="vwoCode">
            {`
        window._vwo_code || (function() {
          var account_id=${vwoProjectID},
              version=2.1,
              settings_tolerance=2000,
              hide_element='body',
              hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
              /* DO NOT EDIT BELOW THIS LINE */
              f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f = true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src = e.src}else{t.text = e.text}d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var i=this.getSettings(),n=d.createElement('script'),r=this;t=t||{};if(i){n.textContent = i;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200){_vwo_code.addScript({ text: o.responseText })}else{_vwo_code.finish('&e=loading_failure:' + e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}_vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t = d.createElement('style');var i=this.hide_element(),n=i?i+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=n;else t.appendChild(d.createTextNode(n));r.appendChild(t)}else{t = d.getElementsByTagName('head')[0];var n=d.createElement('div');n.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;';n.setAttribute('id','_vis_opt_path_hides');n.classList.add('_vis_hide_layer');t.parentNode.insertBefore(n,t.nextSibling)}var o='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({ src: o })}else{this.load(o + '&x=true')}}};w._vwo_code=code;code.init();
        })();
      `}
          </script>
        )}
      </Helmet>
      {seo && (
        <SEO
          title={seo.title}
          titleTemplate={titleTemplate}
          lang={spanish ? 'es-US' : 'en-US'}
          canonical={getCanonical(siteURL, path, seo.canonical)}
          description={seo.description}
          robots={seo.robots || defaultRobots}
          schema={buildSeoSchema(seo, [
            {
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              name: siteName,
              alternateName,
              url: siteURL,
            },
          ])}
        />
      )}
      {header && (
        <Header
          borderColor={newHeaderData.borderColor?.toLowerCase()}
          borderHeight={newHeaderData.borderHeight}
          callToActions={newHeaderData?.callToActions}
          headerBackgroundColor={newHeaderData.headerBackgroundColor
            ?.toLowerCase()
            .replace(' ', '')}
          headerTextColor={newHeaderData.headerTextColor?.toLowerCase()}
          hideLocation={newHeaderData.hideLocation}
          hoverBackgroundColor={newHeaderData.hoverBackgroundColor
            ?.toLowerCase()
            .replace(' ', '')}
          hoverTextColor={newHeaderData.hoverTextColor?.toLowerCase()}
          topBarNavData={newHeaderData?.topBarLinks}
          topBarText={newHeaderData?.topBarText}
          navData={newHeaderData?.linkGroups}
          logo={newHeaderData.logo}
          logoLink={newHeaderData.logoLink}
          navigationBackgroundColor={newHeaderData.navigationBackgroundColor
            ?.toLowerCase()
            .replace(' ', '')}
          navigationTextColor={newHeaderData.navigationTextColor?.toLowerCase()}
          navigationPosition={newHeaderData.navigationPosition}
          topBarBackgroundColor={newHeaderData.topBarBackgroundColor
            ?.toLowerCase()
            .replace(' ', '')}
          navigationVisibility={newHeaderData?.navigationVisibility}
          topBarTextColor={newHeaderData?.topBarTextColor?.toLowerCase()}
          textBlock={newHeaderData?.textBlock}
          textBlockAlignment={newHeaderData?.textBlockAlignment}
        />
      )}
      {main || (
        <Main type={type}>
          {hero && mapSections(hero, heroOptions, packages)}
          {/* TODO: since we never pass options here does that mean those props are never used? */}
          {sections &&
            sections.map((section) =>
              mapSections(section, sectionOptions, packages)
            )}
        </Main>
      )}
      {breadcrumbProps && !hideBreadcrumbs && (
        <Breadcrumbs className="breadcrumbs" {...breadcrumbProps} />
      )}
      {footer && (
        <Footer
          logoLink={newFooterData.logoLink}
          logo={newFooterData.logo}
          navData={newFooterData.linkGroups}
          disclaimers={newFooterData?.disclaimers}
          navigationBackgroundColor={newFooterData.navigationBackgroundColor
            ?.toLowerCase()
            .replace(' ', '')}
          navigationTextColor={newFooterData.navigationTextColor?.toLowerCase()}
          disclaimerBackgroundColor={newFooterData.disclaimerBackgroundColor
            ?.toLowerCase()
            .replace(' ', '')}
          disclaimerTextColor={newFooterData.disclaimerTextColor?.toLowerCase()}
          logoBackgroundColor={newFooterData.logoBackgroundColor
            ?.toLowerCase()
            .replace(' ', '')}
          logoTextColor={newFooterData.logoTextColor?.toLowerCase()}
          logoTopBorder={newFooterData.logoTopBorder?.toLowerCase()}
          logoBottomBorder={newFooterData.logoBottomBorder?.toLowerCase()}
        />
      )}
    </>
  )
}

export default DefaultTemplate
